import { cn } from '@/utils/cn'
import type { SuggestionChip } from '@/types'
import { SuggestionChipItem } from './SuggestionChipItem'

export interface WelcomeScreenProps {
  onSelect: (text: string) => void
  disabled?: boolean
  /** Shows the callback-scheduling starter prompt. Driven by the backend
   * `careadvocate_callback_scheduling_enabled` flag via /config/ui; off by default. */
  careAdvocateCallbackSchedulingEnabled?: boolean
  className?: string
}

// Static starter packs for the empty state. Deliberately small and hardcoded —
// there is no context to be smart about before the first message, so this is a
// curated set, not a generated one. Contextual chips take over after turn one.
type StarterPrompt = { text: string; kind?: SuggestionChip['kind'] }

const STARTER_PACKS: { label: string; prompts: StarterPrompt[] }[] = [
  {
    label: 'Coverage',
    prompts: [
      { text: 'Is this procedure covered under my plan?' },
      { text: "What's my deductible so far?" },
    ],
  },
  {
    label: 'Find care',
    prompts: [
      { text: 'Find an in-network doctor near me' },
      { text: 'Is my current doctor in-network?' },
    ],
  },
  {
    label: 'Cost',
    prompts: [{ text: 'What will this cost me out of pocket?' }],
  },
  {
    label: 'Get care',
    prompts: [
      { text: 'What are my next steps?' },
      { text: 'Talk to a care advocate', kind: 'handoff' },
    ],
  },
]

const CAREADVOCATE_CALLBACK_SCHEDULING_PROMPT: StarterPrompt = {
  text: 'Schedule call with Care Advocate',
}

/** The static packs, with the callback-scheduling prompt added to "Get care"
 * (before the phone handoff, which stays last) when the feature is enabled. */
function buildStarterPacks(
  careAdvocateCallbackSchedulingEnabled: boolean,
): typeof STARTER_PACKS {
  if (!careAdvocateCallbackSchedulingEnabled) return STARTER_PACKS
  return STARTER_PACKS.map((pack) =>
    pack.label === 'Get care'
      ? {
          ...pack,
          prompts: pack.prompts.flatMap((prompt) =>
            prompt.kind === 'handoff'
              ? [CAREADVOCATE_CALLBACK_SCHEDULING_PROMPT, prompt]
              : [prompt],
          ),
        }
      : pack,
  )
}

/**
 * Empty-state welcome shown before any messages exist. Surfaces a few tappable
 * starter prompts so members aren't typing into a blank box.
 */
export function WelcomeScreen({
  onSelect,
  disabled = false,
  careAdvocateCallbackSchedulingEnabled = false,
  className,
}: WelcomeScreenProps) {
  const starterPacks = buildStarterPacks(careAdvocateCallbackSchedulingEnabled)
  return (
    <div
      className={cn(
        'flex h-full flex-col items-center justify-center gap-6 px-4 py-8',
        className,
      )}
    >
      <p className="max-w-sm text-center text-gray-600">
        Hi! I can help with coverage, finding care, and costs. Pick a starting
        point or just type your question.
      </p>

      <div className="w-full max-w-md space-y-4">
        {starterPacks.map((pack) => (
          <div key={pack.label}>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
              {pack.label}
            </p>
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label={`${pack.label} suggestions`}
            >
              {pack.prompts.map((prompt) => (
                <SuggestionChipItem
                  key={prompt.text}
                  text={prompt.text}
                  kind={prompt.kind}
                  onSelect={onSelect}
                  disabled={disabled}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
